import { useCallback, useEffect, useRef, useState } from 'react';

import type { Event, RiveEventPayload } from '@rive-app/react-canvas';
import { EventType, Fit, Layout } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import signUpWelcomeBonusRiv from '../../../../assets/auth/sign_up_welcome_bonus_v4.riv';
import { SocialCoin } from '../../../../components/SocialCoin/SocialCoin';
import { SweepCoin } from '../../../../components/SweepCoin/SweepCoin';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { useCoinsAnimation } from '../../../../hooks/useCoinsAnimation';
import { useMissedWelcomeBonus } from '../../../../hooks/useMissedWelcomeBonus';
import { analytics } from '../../../../services/analytics';
import { useFlag } from '../../../../services/launchDarkly';
import type { ClaimableReward } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';
import { formatValueWithK } from '../../../../utils/helpers';
import { devError } from '../../../../utils/log';

import styles from './WelcomeBonusRedesign.module.scss';
import { WelcomeBonusStaticContent } from './WelcomeBonusStaticContent';

const STATE_MACHINE_NAME = 'State Machine 1';
const ARTBOARD_NAME = 'Gift';
const INPUT_CC = 'is_cc';
const ACTIVATE = 'activate';
const RIVE_FALLBACK_TIMEOUT_MS = 1000;

type Props = WizardChildProps | { componentType: 'dialog' | 'portal' } | Record<string, never>;

export const WelcomeBonusRedesign = observer(function WelcomeBonusRedesign(props: Props) {
  const { removeMissedWelcomeBonusUserData } = useMissedWelcomeBonus();
  const [showCoins, setShowCoins] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showStaticFallback, setShowStaticFallback] = useState(false);
  const { t } = useTranslation();
  const { rewardsStore, portalStore, uiStore, walletStore } = useStores();
  const { startAnimation } = useCoinsAnimation();

  const isScModeAvailable = useFlag<boolean>('sc_mode_available', true);
  const isRiveStaticFallbackEnabled = useFlag<boolean>('registration_flow_rive_static_fallback', false);
  const componentType = 'componentType' in props ? props.componentType : null;
  const isCurrentWizardStep = 'isCurrent' in props ? props.isCurrent : false;
  const isActive = isCurrentWizardStep || componentType === 'dialog';

  useEffect(() => {
    if (isActive) {
      void analytics.sendEvent('welcome_gift', {
        event_category: 'sign_up',
        event_action: 'impression',
        button_text: 'claim',
      });
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      removeMissedWelcomeBonusUserData();
    }
  }, [removeMissedWelcomeBonusUserData, isActive]);

  useEffect(() => {
    if (!rewardsStore.welcomeBonusLoading && !rewardsStore.welcomeBonus) {
      devError('WelcomeBonusDialog: welcomeBonus is not loaded, closing dialog');
      portalStore.closePortal();
    }
  }, [rewardsStore.welcomeBonus, rewardsStore.welcomeBonusLoading, portalStore]);

  useEffect(() => {
    walletStore.setCustomBalance({
      gcCoin: 0,
      scCoin: 0,
      cash: 0,
    });
    return () => {
      walletStore.clearCustomBalance();
    };
  }, [walletStore]);

  const gcAmount = formatValueWithK(rewardsStore.welcomeBonus?.gcAmount || 0, t);
  const scAmount = (isScModeAvailable ? rewardsStore.welcomeBonus?.scAmount || 0 : 0).toLocaleString();

  const closeDialog = useCallback(() => {
    void analytics.sendEvent('welcome_gift', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'claim',
    });
    if (props && 'componentType' in props && props.componentType === 'dialog') {
      uiStore.setOpenDialog({ dialog: null });
      return;
    }
    portalStore.closePortal();
  }, [portalStore, uiStore, props]);

  const hasSubmittedRef = useRef(false);

  const onSubmit = useCallback(() => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;

    const welcomeBonus = rewardsStore.welcomeBonus;
    if (welcomeBonus) {
      const rewards: ClaimableReward[] = [
        {
          gcCoin: welcomeBonus.gcAmount || 0,
          scCoin: isScModeAvailable ? welcomeBonus.scAmount || 0 : 0,
          bundleItems: [],
        },
      ];

      walletStore.clearCustomBalance();

      startAnimation({
        rewards,
        enableNewRewardsAnimation: true,
        onEnd: () => {
          setIsClosing(true);
          setTimeout(() => {
            closeDialog();
          }, 200);
        },
      });
    } else {
      closeDialog();
    }
  }, [rewardsStore.welcomeBonus, isScModeAvailable, walletStore, startAnimation, closeDialog]);

  const onRiveEvent = useCallback(
    (event: Event) => {
      const data = (event?.data || {}) as RiveEventPayload;
      if (data.name && data.name === 'finish') {
        onSubmit();
      }
    },
    [onSubmit],
  );

  const { rive, RiveComponent, setRiveViewModelBoolean } = useRiveMachine({
    src: signUpWelcomeBonusRiv,
    artboard: ARTBOARD_NAME,
    stateMachineName: STATE_MACHINE_NAME,
    autoplay: false,
    autoBind: true,
    layout: new Layout({
      fit: Fit.Contain,
    }),
    loadingTimeout: RIVE_FALLBACK_TIMEOUT_MS,
    onLoadError: () => {
      if (isRiveStaticFallbackEnabled) {
        setShowStaticFallback(true);
      } else if (isActive) {
        onSubmit();
      }
    },
  });

  useEffect(() => {
    if (rive && isActive) {
      rive.off(EventType.RiveEvent, onRiveEvent);
      rive.on(EventType.RiveEvent, onRiveEvent);

      rive.setTextRunValue('welcomeTo', t('welcome.welcomeTo'));
      rive.setTextRunValue('crownCoinsCasino', `${String(t('common.crownCoinsCasino'))}!`);
      rive.setTextRunValue('claimYourBonus', t('welcome.claimYourBonus'));
      rive.setTextRunValue('claim', t('common.buttons.claim'));
      setRiveViewModelBoolean(INPUT_CC, !isScModeAvailable);
      setRiveViewModelBoolean(ACTIVATE, true);
      setShowCoins(true);
      rive.play();

      return () => {
        rive.off(EventType.RiveEvent, onRiveEvent);
      };
    }
  }, [rive, onRiveEvent, setRiveViewModelBoolean, isScModeAvailable, isActive, t]);

  if (showStaticFallback && rewardsStore.welcomeBonus) {
    return (
      <div className={classNames(styles.container, { [styles.closing]: isClosing })} data-testid="welcome-bonus">
        <WelcomeBonusStaticContent
          gcAmount={gcAmount}
          isScVisible={!!rewardsStore.welcomeBonus.scAmount && isScModeAvailable}
          scAmount={scAmount}
          showGcAmount={!!rewardsStore.welcomeBonus.gcAmount}
          onClaim={onSubmit}
        />
      </div>
    );
  }

  return (
    <div className={classNames(styles.container, { [styles.closing]: isClosing })} data-testid="welcome-bonus">
      <div className={styles.riveWrapper}>
        <RiveComponent className={styles.rive} />
        <div className={classNames(styles.coinsContainer, { [styles.coinsVisible]: showCoins })}>
          {!!rewardsStore.welcomeBonus?.gcAmount && (
            <div className={styles.coinItem}>
              <SocialCoin isPile alt="Gold Coins" className={styles.coinIcon} />
              <span className={styles.coinAmount} data-testid="welcome-bonus-cc-amount">
                {gcAmount}
              </span>
            </div>
          )}
          {!!rewardsStore.welcomeBonus?.scAmount && isScModeAvailable && (
            <div className={styles.coinItem}>
              <SweepCoin isPile alt="Sweep Coins" className={styles.coinIcon} />
              <span className={styles.coinAmount} data-testid="welcome-bonus-sc-amount">
                {scAmount}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
